import FJModalClose from '../../../components/FJModals/FJModalClose/FJModalClose';
import styles from './FJVideoTrimModel.module.less';
import FJLocalStore from '../../../store/FJLocalStore';
import FJButton, { FJButtonType } from '../../components/FJButton/FJButton';
import { useEffect, useRef, useCallback, useState } from 'react';
import FJPlayIcon from '../../../assets/svg/crop-play.svg?react';
import FJPauseIcon from '../../../assets/svg/crop-pause.svg?react';
import { renderThumbnails } from '../../../store/FJVideoThumbnailStore';
import FJConstant from '../../../constants/FJConstant';
import FJResourceItemLoading from '../../components/FJResourceItemLoading/FJResourceItemLoading';
import FJUtil from '../../../util/FJUtil';
import FJTrimDragBarBox from './FJTrimDragBarBox';
import _ from 'lodash';

const FJVideoTrimModel = props => {
    const { onClose, onCancel, onApply, data, active, maxDuration, minDuration = 1 } = props;

    const { src, width, height, duration, start, end } = data;
    const thumbnailCanvasRef = useRef(null);
    const _thumbnailAbortController = useRef(null);
    const videoRef = useRef(null);
    const timer = useRef(null);
    const resizeDebounce = useRef(null);

    const isPlayingRef = useRef(false);
    const dragProgressUpIsPlayingRef = useRef(false);
    const trimBoxRelativeRectRef = useRef({
        left: 0,
        top: 0,
        width: 1,
        height: 0,
    });

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentProgress, setCurrentProgress] = useState(0);
    const [loadVideoStatus, setLoadVideoStatus] = useState(FJConstant.LOADING);
    const [trimLoading, setTrimLoading] = useState(false);
    const [trimBoxRelativeRect, setTrimBoxRelativeRect] = useState({
        left: 0,
        top: 0,
        width: 1,
        height: 0,
    });

    const updateThumbnailCanvas = useCallback(async () => {
        if (!active) {
            return;
        }

        const thumbnailCanvas = thumbnailCanvasRef.current;

        if (!thumbnailCanvas) {
            return;
        }

        if (_thumbnailAbortController.current) {
            _thumbnailAbortController.current.abort();
        }

        const thumbnailCanvasWidth = thumbnailCanvas.getBoundingClientRect().width;
        const thumbnailCanvasHeight = thumbnailCanvas.getBoundingClientRect().height;
        if (thumbnailCanvasWidth === 0 || thumbnailCanvasHeight === 0) {
            return;
        }

        thumbnailCanvas.width = thumbnailCanvasWidth;
        thumbnailCanvas.height = thumbnailCanvasHeight;

        const thumbnailSize = {
            width: Math.floor(thumbnailCanvasHeight * (width / height)),
            height: thumbnailCanvasHeight,
        };

        await renderThumbnails(src, thumbnailCanvas, {
            thumbnailSize: thumbnailSize,
            renderInfo: null,
            fileInfo: { width, height },
            start: 0,
            end: duration,
            setAbortController: abortController => {
                _thumbnailAbortController.current = abortController;
            },
        });
    }, [src, width, height, duration, active]);

    useEffect(() => {
        setTimeout(() => {
            updateThumbnailCanvas();
        }, 200);
    }, [updateThumbnailCanvas]);

    const initTrimBoxRect = useCallback(() => {
        const trimBoxRelativeRect = {
            left: Math.max(start / duration, 0),
            top: 0,
            width: Math.min((end - start) / duration, 1),
            height: 1,
        };

        setTrimBoxRelativeRect(trimBoxRelativeRect);
        trimBoxRelativeRectRef.current = trimBoxRelativeRect;
    }, [duration, start, end]);

    const pauseVideo = useCallback(() => {
        videoRef.current.pause();
        window.cancelAnimationFrame(timer.current);
        setIsPlaying(false);
        isPlayingRef.current = false;
    }, []);

    const playVideo = useCallback(() => {
        videoRef.current.play();
        setIsPlaying(true);
        isPlayingRef.current = true;

        let currentTime = videoRef.current.currentTime;
        let requestAnimationTime = null;
        const maxTime = duration * (trimBoxRelativeRectRef.current.left + trimBoxRelativeRectRef.current.width);
        const minTime = duration * trimBoxRelativeRectRef.current.left;

        let __playing = timestamp => {
            if (!requestAnimationTime) requestAnimationTime = timestamp;

            currentTime += (timestamp - requestAnimationTime) / 1000;
            requestAnimationTime = timestamp;

            if (currentTime >= 0 && currentTime <= maxTime) {
                setCurrentProgress(currentTime / duration);
            }

            if (currentTime >= maxTime) {
                videoRef.current.currentTime = minTime;
                currentTime = minTime;
                setCurrentProgress(currentTime / duration);
                pauseVideo();
            } else {
                timer.current = window.requestAnimationFrame(__playing);
            }
        };

        if (timer.current) window.cancelAnimationFrame(timer.current);
        timer.current = window.requestAnimationFrame(__playing);
    }, [duration, pauseVideo]);

    const changeTrimBoxRect = useCallback(
        (newTrimBoxRect, dragType) => {
            setTrimBoxRelativeRect(newTrimBoxRect);
            trimBoxRelativeRectRef.current = newTrimBoxRect;
            pauseVideo();

            if (dragType === 'right') {
                videoRef.current.currentTime = (newTrimBoxRect.left + newTrimBoxRect.width) * duration;
                setCurrentProgress(newTrimBoxRect.left + newTrimBoxRect.width);
            } else {
                videoRef.current.currentTime = newTrimBoxRect.left * duration;
                setCurrentProgress(newTrimBoxRect.left);
            }
        },
        [duration, pauseVideo],
    );

    const handleKeyDown = useCallback(
        event => {
            if (event.key === ' ' || event.key === 'Spacebar') {
                event.preventDefault();
                if (isPlaying) {
                    pauseVideo();
                } else {
                    playVideo();
                }
            }
        },
        [isPlaying, pauseVideo, playVideo],
    );

    const handleResize = useCallback(() => {
        if (!resizeDebounce.current) {
            resizeDebounce.current = _.debounce(() => {
                updateThumbnailCanvas();
            }, 200);
        }
        resizeDebounce.current();
    }, [updateThumbnailCanvas]);

    const _onApply = useCallback(() => {
        setTrimLoading(true);
        pauseVideo();
        onApply &&
            onApply(trimBoxRelativeRect, () => {
                setTrimLoading(false);
            });
    }, [onApply, trimBoxRelativeRect, pauseVideo]);

    useEffect(() => {
        initTrimBoxRect();
    }, [initTrimBoxRect]);

    const autoPlay = useCallback(() => {
        if (videoRef.current) {
            videoRef.current.currentTime = start;
            setCurrentProgress(start / duration);
            playVideo();
        }
    }, [playVideo, start, duration]);

    useEffect(() => {
        if (active) {
            window.addEventListener('keydown', handleKeyDown);
            window.addEventListener('resize', handleResize);
        } else {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', handleResize);
        }
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', handleResize);
        };
    }, [active, handleKeyDown, handleResize]);

    const _onContentClick = useCallback(
        e => {
            e.stopPropagation();
            if (isPlaying) {
                pauseVideo();
            } else {
                playVideo();
            }
        },
        [isPlaying, pauseVideo, playVideo],
    );

    const _onDragChangeProgress = useCallback(
        (newProgress, dragType) => {
            if (dragType === 'mousedown') {
                dragProgressUpIsPlayingRef.current = isPlayingRef.current;
            }

            pauseVideo();
            if (newProgress !== null && newProgress !== undefined) {
                videoRef.current.currentTime = newProgress * duration;
                setCurrentProgress(newProgress);
            }

            if (dragType === 'mouseup' && dragProgressUpIsPlayingRef.current) {
                playVideo();
            }
        },
        [duration, pauseVideo, playVideo],
    );

    return (
        <div className={styles.container}>
            <FJModalClose onClick={onClose} />

            <div className={styles.header}>
                <div className={styles.header_title}>{FJLocalStore._('trim-video')}</div>
                <div className={styles.header_desc}>
                    {FJLocalStore._('please-select-a-single-footage-to-get-better-ai-sound-effects')}
                </div>
            </div>

            <div className={styles.content} onClick={_onContentClick}>
                <video
                    src={src}
                    width={width}
                    height={height}
                    ref={videoRef}
                    onLoadedData={() => {
                        setLoadVideoStatus(FJConstant.SUCCESS);
                        autoPlay();
                    }}
                />
                {loadVideoStatus === FJConstant.LOADING && <FJResourceItemLoading className={styles.loading} />}
            </div>

            <div className={styles.control}>
                <div className={styles.control_left}>
                    <div className={styles.btn} onClick={isPlaying ? pauseVideo : playVideo}>
                        {isPlaying ? (
                            <FJPauseIcon className={styles.btn_play_icon} />
                        ) : (
                            <FJPlayIcon className={styles.btn_play_icon} />
                        )}
                    </div>
                    <div className={styles.control_left_time}>
                        <span className={styles.control_left_time_start}>
                            {FJUtil.formatTime(currentProgress * duration, false, 2)}
                        </span>
                        <span className={styles.control_left_time_end}> / {FJUtil.formatTime(duration, false, 2)}</span>
                    </div>
                </div>

                <div className={styles.control_right}>
                    <canvas ref={thumbnailCanvasRef} className={styles.thumbnail_canvas} />

                    <FJTrimDragBarBox
                        currentProgress={currentProgress}
                        maxRelativeWidth={maxDuration / duration}
                        minRelativeWidth={minDuration / duration}
                        relativeRect={trimBoxRelativeRect}
                        changeRelativeRect={changeTrimBoxRect}
                        duration={duration}
                        progress={currentProgress}
                        changeProgress={_onDragChangeProgress}
                    />
                </div>
            </div>

            <div className={styles.footer_btn}>
                <FJButton
                    type={FJButtonType.DEFAULT}
                    className={styles.footer_btn_cancel}
                    onClick={onCancel}
                    text={FJLocalStore._('cancel')}
                    textClassName={styles.footer_btn_text}
                />
                <FJButton
                    type={FJButtonType.GRADIENT}
                    className={styles.footer_btn_apply}
                    onClick={_onApply}
                    text={FJLocalStore._('apply')}
                    isLoading={trimLoading}
                    textClassName={styles.footer_btn_text}
                />
            </div>
        </div>
    );
};

export default FJVideoTrimModel;
